/**
 * Chat Store - Buyer PWA
 * Manages chat threads, messages, typing indicators
 */

import { create } from 'zustand';
import { logger } from '@/lib/logger';
import * as api from '@/lib/api';

export interface ChatMessage {
  id: string;
  sender_role: string;
  sender_id: string;
  message: string;
  attachments: any[];
  created_at: string;
}

export interface ChatThread {
  thread_id: string;
  order_id: string;
  closed_at: string | null;
  created_at: string;
  last_message: {
    sender_role: string;
    message: string;
    created_at: string;
  } | null;
  unread_count: number;
}

interface ChatState {
  // Threads
  threads: ChatThread[];
  isLoadingThreads: boolean;
  
  // Current thread
  currentThreadId: string | null;
  messages: ChatMessage[];
  isLoadingMessages: boolean;
  
  // Typing indicators
  typingUsers: Map<string, string>; // threadId -> userRole
  
  // Actions
  fetchThreads: () => Promise<void>;
  selectThread: (threadId: string) => void;
  fetchMessages: (threadId: string) => Promise<void>;
  sendMessage: (threadId: string, message: string) => Promise<void>;
  setTyping: (threadId: string, isTyping: boolean) => void;
  addMessage: (threadId: string, message: ChatMessage) => void;
  updateTypingIndicator: (threadId: string, userRole: string, isTyping: boolean) => void;
  clearChat: () => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  threads: [],
  isLoadingThreads: false,
  currentThreadId: null,
  messages: [],
  isLoadingMessages: false,
  typingUsers: new Map(),

  fetchThreads: async () => {
    set({ isLoadingThreads: true });
    try {
      const response = await api.getChatThreads();
      set({ threads: response.threads, isLoadingThreads: false });
      logger.info('Chat threads loaded', { count: response.threads.length });
    } catch (error) {
      logger.error('Failed to fetch chat threads', error instanceof Error ? error : new Error(String(error)));
      set({ isLoadingThreads: false });
      throw error;
    }
  },

  selectThread: (threadId: string) => {
    set({ currentThreadId: threadId, messages: [] });
    get().fetchMessages(threadId);
  },

  fetchMessages: async (threadId: string) => {
    set({ isLoadingMessages: true });
    try {
      const response = await api.getChatMessages(threadId);
      set({ messages: response.messages, isLoadingMessages: false });
      logger.info('Chat messages loaded', { threadId, count: response.messages.length });
    } catch (error) {
      logger.error('Failed to fetch chat messages', error instanceof Error ? error : new Error(String(error)));
      set({ isLoadingMessages: false });
      throw error;
    }
  },

  sendMessage: async (threadId: string, message: string) => {
    try {
      const response = await api.sendChatMessage(threadId, message);
      logger.info('Message sent', { threadId, messageId: response.message_id });
      
      // Refresh messages after sending
      await get().fetchMessages(threadId);
    } catch (error) {
      logger.error('Failed to send message', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  },

  setTyping: async (threadId: string, isTyping: boolean) => {
    try {
      await api.sendTypingIndicator(threadId, isTyping);
    } catch (error) {
      logger.error('Failed to send typing indicator', error instanceof Error ? error : new Error(String(error)));
    }
  },

  addMessage: (threadId: string, message: ChatMessage) => {
    if (get().currentThreadId === threadId) {
      set((state) => ({
        messages: [...state.messages, message],
      }));
    }
    
    // Update thread's last message and unread count
    set((state) => ({
      threads: state.threads.map((thread) =>
        thread.thread_id === threadId
          ? {
              ...thread,
              last_message: {
                sender_role: message.sender_role,
                message: message.message,
                created_at: message.created_at,
              },
              unread_count: thread.unread_count + 1,
            }
          : thread
      ),
    }));
  },

  updateTypingIndicator: (threadId: string, userRole: string, isTyping: boolean) => {
    set((state) => {
      const newTypingUsers = new Map(state.typingUsers);
      if (isTyping) {
        newTypingUsers.set(threadId, userRole);
      } else {
        newTypingUsers.delete(threadId);
      }
      return { typingUsers: newTypingUsers };
    });
  },

  clearChat: () => {
    set({
      threads: [],
      currentThreadId: null,
      messages: [],
      typingUsers: new Map(),
    });
  },
}));

